import type { KeyedMutator } from 'swr';

import { ActivityAllocationTabProps, ActivityDTO } from 'src/types/dto';
import type { TabConfig } from 'src/components/tabbed-modal';

import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useMemo, useCallback } from 'react';
import type { MouseEvent } from 'react';

import { Box, Grid, Paper, Stack, Button, Typography, Menu, MenuItem, Divider, IconButton, Tooltip } from '@mui/material';

import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';

import axios, { endpoints } from 'src/utils/axios';

import { useGetVisit } from 'src/actions/visit';

import { TabbedModal } from 'src/components/tabbed-modal';
import { SplashScreen } from 'src/components/loading-screen';
import { EntityAuditTrail } from 'src/components/entity-audit-trail';
import { Iconify } from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { NonCompliantApprovalDialog } from 'src/components/non-compliant-approval-dialog';
import CompetencyModal from 'src/components/competency-modal';
import { mapActivityCompetencyToModalItem } from 'src/types/mappers/competency.mapper';
import { resolveActivityStandardTemplateId, resolveActivitySchemeLabel } from 'src/utils/competency';
import { toast } from 'src/components/snackbar';

import { AuditTrailsTab } from 'src/sections/organization/audit-trails-tab';
import { AllocationAuditTrailsTab } from './allocation-audit-trails-tab';

import { PermissionKeys } from 'src/auth/types';

import { ActivityDetails } from './activity-details';
import { AppointmentsDetails } from './appointments-details';
import { ActivityLocationMap } from './activity-location-map';
import { VisitAllocationTab } from './visit-allocation-tab';
import { useUserPermissions } from 'src/hooks/use-user-permissions';
import { useCanUpdateOwnedActivity } from 'src/hooks/use-can-update-owned-activity';
import { useAuthContext } from 'src/auth/hooks';
import {
  canUpdateServiceForOwner,
  useUpdateServicePermissions,
} from 'src/hooks/use-can-update-service';
import { SchemesDetails } from './schemes-details';
import { useSingleActivityStatusManagement } from 'src/hooks/use-activity-status-management';
import { ActivityNewEditForm } from '../visit-new-edit-form';
import { ActivityDatesTab } from './activity-dates';
import type { ICompetencyItem } from 'src/types/competency';

const isActivityDatesTabEnabled = process.env.NEXT_PUBLIC_ENABLE_ACTIVITY_DATES_TAB === 'true';

interface ActivityDetailsModalProps {
  open: boolean;
  onClose: () => void;
  id: number | string;
  tabId?: 'details' | 'audit-trails' | 'appointments' | 'allocation' | 'dates' | 'allocation-audit-trail';
  allocationTabProps?: ActivityAllocationTabProps;
  onSelectActivity?: (activity: ActivityDTO) => void;
  solutionId?: number | string;
  onNavigateUp?: () => void;
  onNavigateDown?: () => void;
  onOpenEditActivity?: (activity: ActivityDTO) => void;
}

function DetailsTabContent({
  visit,
  mutateVisit,
  showAuditTrail,
  canEditCompetencies,
  onEditCompetencies,
}: {
  visit: ActivityDTO;
  mutateVisit: KeyedMutator<ActivityDTO>;
  showAuditTrail: boolean;
  canEditCompetencies: boolean;
  onEditCompetencies: () => void;
}) {
  const { t } = useTranslation('activities');

  const siteLocation = visit.site?.location;

  return (
    <Grid container sx={{ height: '100%' }}>
      <Grid item xs={12} md={3} sx={{ borderRight: 1, borderColor: 'divider', height: '100%' }}>
        <Paper 
          sx={{ 
            height: '100%', 
            overflow: 'hidden',
            borderRadius: 0,
            borderColor: 'divider'
          }}
        >
          {siteLocation ? (
            <ActivityLocationMap
              location={siteLocation}
              siteName={visit.site?.name}
              activityStatus={visit.status}
              appointments={visit.appointments}
              allocatedInspectors={visit.allocatedInspectors}
            />
          ) : (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
                p: 2,
                textAlign: 'center',
              }}
            >
              <Typography variant="body2">
                {t('activityModal.details.sections.map.noLocationData')}
              </Typography>
            </Box>
          )}
        </Paper>
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          borderRight: 1,
          borderColor: 'divider',
          px: 2,
          pt: 2,
          height: '100%',
          overflow: 'auto',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', minHeight: 40, mb: 1, minWidth: 0 }}>
          <Typography
            variant="h4"
            sx={{
              lineHeight: 1.2,
              minWidth: 0,
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            {visit.name}
          </Typography>
        </Box>
        <ActivityDetails
          visit={visit}
          mutateVisit={mutateVisit}
          canEditCompetencies={canEditCompetencies}
          onEditCompetencies={onEditCompetencies}
        />
      </Grid>
      {showAuditTrail && (
        <Grid
          item
          xs={12}
          md={3}
          display="flex"
          flexDirection="column"
          sx={{ height: '100%', overflow: 'auto' }}
        >
          <Typography
            variant="h6"
            display="block"
            sx={{ borderBottom: 1, borderColor: 'divider', py: 1, pl: 2 }}
          >
            {t('activityModal.auditTrail')}
          </Typography>
          <EntityAuditTrail entityId={visit?.id} entityType="Activity" endpoint={endpoints.activities.auditTrail(visit?.id)} />
        </Grid>
      )}
    </Grid>
  );
}

export function ActivityDetailsModal({
  open,
  onClose,
  id: activityId,
  tabId = 'details',
  allocationTabProps,
  onSelectActivity,
  solutionId,
  onNavigateUp,
  onNavigateDown,
  onOpenEditActivity,
}: ActivityDetailsModalProps) {
  const { t } = useTranslation('activities');
  const { visit, visitLoading, mutateVisit } = useGetVisit(activityId.toString(), solutionId);

  // When opened from inside a solution the modal is a read-only review of the
  // proposed allocation: only Details + Appointments tabs, no mutating actions,
  // and the Appointments tab shows the solution appointments.
  const isSolutionContext = solutionId != null;

  useEffect(() => {
    if (open) {
      mutateVisit();
    }
  }, [open, mutateVisit]);

  const { user } = useAuthContext();
  const { hasAnyPermission } = useUserPermissions();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);
  const [requestJustSucceeded, setRequestJustSucceeded] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openCompetenciesModal, setOpenCompetenciesModal] = useState(false);

  const handleClose = useCallback(() => {
    setRequestJustSucceeded(false);
    setOpenEditModal(false);
    onClose();
  }, [onClose]);

  const {
    getActionsForStatus,
    confirmDelete,
    handleConfirmDelete,
    handleCancelDelete,
    confirmCancel,
    handleConfirmCancel,
    handleCancelCancelDialog,
    confirmUnallocate,
    handleConfirmUnallocate,
    handleCancelUnallocate,
    confirmNonCompliant,
    nonCompliantFeedback,
    handleConfirmNonCompliant,
    handleCancelNonCompliant,
    allocationIntervalRef,
    isProcessing,
  } = useSingleActivityStatusManagement({
    activity: visit,
    activityId: Number(activityId),
    onRefetch: mutateVisit,
    onClose: handleClose,
    onSelectActivity,
    onPreRequestOrRequestSuccess: () => setRequestJustSucceeded(true),
  });

  const updateServicePermissions = useUpdateServicePermissions();
  const hasUpdateActivityPermission = canUpdateServiceForOwner(
    visit?.standard?.project?.owner?.id,
    user?.id,
    updateServicePermissions
  );

  const handleMenuClick = (event: MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleEditClick = useCallback(() => {
    setOpenEditModal(true);
    handleMenuClose();
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setOpenEditModal(false);
    mutateVisit();
  }, [mutateVisit]);

  const handleSaveCompetencies = useCallback(
    async (competencies: ICompetencyItem[]) => {
      if (!visit?.id) return;
      try {
        await axios.post(endpoints.activities.competencies(visit.id), {
          competencies: competencies.map((competency) => ({
            competencyId: competency.id,
            level: competency.level,
          })),
        });
        toast.success(t('competencyModal.toast.success'));
        mutateVisit();
      } catch {
        toast.error(t('competencyModal.toast.error'));
      } finally {
        setOpenCompetenciesModal(false);
      }
    },
    [visit?.id, mutateVisit, t]
  );

  const currentActions = getActionsForStatus(visit?.status);
  const router = useRouter();

  const loadBalancingParams = useMemo(() => {
    if (!visit) return null;
    const hasAppointments = (visit.appointments?.length ?? 0) > 0;
    const hasAllocated = (visit.allocatedInspectors?.length ?? 0) > 0;
    if (!hasAppointments && !hasAllocated) return null;
    let ids: number[] = [];
    let dates: string[] = [];
    if (hasAppointments && visit.appointments) {
      const byInspector = new Map<number, { from: string }>();
      const allFromDates: string[] = [];
      visit.appointments.forEach((app) => {
        const fromStr =
          typeof app.from === 'string' ? app.from : dayjs(app.from).toISOString();
        const d = dayjs(fromStr);
        if (d.isValid()) allFromDates.push(fromStr);
        const inspectorId = app.inspector?.id;
        if (inspectorId !== undefined && !byInspector.has(inspectorId)) {
          byInspector.set(inspectorId, { from: fromStr });
        }
      });
      ids = Array.from(byInspector.keys());
      dates = allFromDates;
    } else if (hasAllocated && visit.allocatedInspectors) {
      ids = visit.allocatedInspectors.map((i) => i.id);
      dates = (visit.allocatedInspectors ?? [])
        .map((i) => i.slots?.[0]?.from)
        .filter(Boolean)
        .map((x) => (typeof x === 'string' ? x : dayjs(x).toISOString()))
        .filter((s) => dayjs(s).isValid());
    }
    if (ids.length === 0) return null;
    const earliestDate =
      dates.length > 0
        ? dayjs(dates.reduce((a, b) => (dayjs(a).isBefore(dayjs(b)) ? a : b))).format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD');
    return { ids, date: earliestDate };
  }, [visit]);

  const handleLoadBalancingClick = useCallback(() => {
    if (!loadBalancingParams) return;
    const params = new URLSearchParams({ 'inspector-ids': loadBalancingParams.ids.join(',') });
    params.set('date', loadBalancingParams.date);
    if (visit?.site?.timezoneId) {
      params.set('timezone', visit.site.timezoneId);
    }
    onClose();
    router.push(`${paths.dashboard.schedule.root}?${params.toString()}`);
  }, [loadBalancingParams, visit?.site?.timezoneId, onClose, router]);

  useEffect(() => {
    return () => {
      if (allocationIntervalRef.current) {
        clearInterval(allocationIntervalRef.current);
      }
    };
  }, [allocationIntervalRef]);

  const tabs: TabConfig[] = [
    {
      id: 'details',
      label: t('activityModal.details.title'),
      icon: 'solar:list-bold',
      component: () => visit ? (
        <DetailsTabContent
          visit={visit}
          mutateVisit={mutateVisit}
          showAuditTrail={hasAnyPermission([{permission: PermissionKeys.VIEW_ACTIVITY_AUDIT_TRAIL}])}
          canEditCompetencies={hasUpdateActivityPermission}
          onEditCompetencies={() => setOpenCompetenciesModal(true)}
        />
      ) : null,
    },
    {
      id: 'appointments',
      label: t('activityModal.appointments.title'),
      icon: 'ic:outline-people',
      component: () => visit ? <AppointmentsDetails visit={visit} solutionId={solutionId} /> : null,
    },
    ...(!isSolutionContext && hasUpdateActivityPermission ? [{
      id: 'allocation',
      label: t('activityModal.schedule.title'),
      icon: 'ic:outline-calendar-month',
      component: () => visit ? (
        <VisitAllocationTab
          activity={visit}
          {...allocationTabProps}
          onSubmit={
            allocationTabProps?.onSubmit
              ? (data) => allocationTabProps.onSubmit!(data, visit.id)
              : undefined
          }
          type={visit.appointments?.length ? 'visit' : 'allocatedVisit'}
          onClose={onClose}
          onActivityUpdate={mutateVisit}
        />
      ) : null,
    }] : []),
    ...(!isSolutionContext && isActivityDatesTabEnabled
      ? [
          {
            id: 'dates',
            label: t('activityModal.dates.title'),
            icon: 'ic:outline-date-range',
            component: () =>
              visit ? (
                <ActivityDatesTab
                  activity={visit}
                  onActivityUpdate={() => void mutateVisit()}
                />
              ) : null,
          },
        ]
      : []),
    // ...(hasAnyPermission([{permission: PermissionKeys.UPDATE_SERVICE, levels: ['GROUP', 'ALL']}]) ? [{
    //   id: 'schemes',
    //   label: t('activityModal.schemes.title'),
    //   icon: 'ic:outline-stars',
    //   component: () => visit ? (
    //     <SchemesDetails
    //       projectId={visit.standard?.project?.id?.toString() ?? ''}
    //       activity={visit}
    //       onUpdate={onClose}
    //     />
    //   ) : null,
    // }]: []),
    ...(!isSolutionContext && hasAnyPermission([{permission: PermissionKeys.VIEW_ACTIVITY_AUDIT_TRAIL}]) ? [{
      id: 'audit-trails',
      label: t('activityModal.auditTrail'),
      icon: 'ic:baseline-chat-bubble-outline',
      component: () => visit ? (
        <Stack px={2} sx={{ minWidth: 0 }}>
          <Typography
            variant="h4"
            py={2}
            sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
          >
            {visit.name}
          </Typography>
          <AuditTrailsTab
            entityFilters={[['Activity', visit.id]]}
            hideEntityTypesFilter
            hideIdColumn
            hideSearchAndFilters
            endpoint={endpoints.activities.auditTrail(visit?.id)}
          />
        </Stack>
      ) : null,
    }] : []),
    ...(isSolutionContext
      ? [
          {
            id: 'allocation-audit-trail',
            label: t('activityModal.allocationAuditTrail.title'),
            icon: 'ic:baseline-chat-bubble-outline',
            component: () =>
              visit ? (
                <Stack px={2} sx={{ minWidth: 0 }}>
                  <Typography
                    variant="h4"
                    py={2}
                    sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                  >
                    {visit.name}
                  </Typography>
                  <AllocationAuditTrailsTab
                    solutionId={solutionId as number | string}
                    activityId={visit.id}
                  />
                </Stack>
              ) : null,
          },
        ]
      : []),
  ];

  if (visitLoading) return <SplashScreen />;

  // Don't render anything when modal is closed to prevent unnecessary API calls
  if (!open) return null;

  return (
    <>
      <TabbedModal
        title={`${visit?.name} #${visit?.id}`}
        open={open}
        onClose={handleClose}
        tabs={tabs}
        defaultTab={tabs.some((tab) => tab.id === tabId) ? tabId : 'details'}
        maxWidth="xl"
        disableEnforceFocus={confirmNonCompliant}
        onNavigateUp={onNavigateUp}
        onNavigateDown={onNavigateDown}
        headerActions={
          !isSolutionContext && hasUpdateActivityPermission ? (
            <Box display="flex" gap={1} alignItems="stretch">
              {hasUpdateActivityPermission &&
                currentActions.visibleActions.map((action) => {
                  const isAllocateAction = action.icon === 'mdi:creation';
                  const isPreRequestOrRequest = ('id' in action && (action.id === 'preRequest' || action.id === 'request'));
                  // action.disabled carries the booking-request lock (status progression is
                  // blocked while the n8n workflow runs); the backend rejects it anyway.
                  const disabled =
                    isProcessing ||
                    Boolean(action.disabled) ||
                    (isPreRequestOrRequest && requestJustSucceeded);
                  const button = (
                    <Button
                      key={action.label}
                      variant="contained"
                      color="inherit"
                      startIcon={<Iconify icon={action.icon} color={disabled ? 'grey.500' : undefined} />}
                      onClick={(e) => {
                        if (isAllocateAction && onSelectActivity && visit) {
                          handleClose();
                          onSelectActivity(visit);
                        }
                        action.onClick();
                      }}
                      disabled={disabled}
                      sx={{
                        whiteSpace: 'nowrap',
                        '&.Mui-disabled': {
                          opacity: 0.5,
                        },
                      }}
                    >
                      {action.label}
                    </Button>
                  );
                  if (!action.disabled || !action.disabledTooltip) {
                    return button;
                  }
                  // Disabled buttons emit no pointer events; the span keeps the tooltip alive.
                  return (
                    <Tooltip key={action.label} title={action.disabledTooltip}>
                      <span>{button}</span>
                    </Tooltip>
                  );
                })}
              {hasUpdateActivityPermission && loadBalancingParams && (
                <Button
                    variant="contained"
                    color="inherit"
                    startIcon={<Iconify icon="mdi:scale-balance" />}
                    onClick={handleLoadBalancingClick}
                    disabled={isProcessing}
                    sx={{
                      whiteSpace: 'nowrap',
                      '&.Mui-disabled': {
                        opacity: 0.5,
                      },
                    }}
                  >
                    {t('allocation.loadBalancing')}
                  </Button>
              )}
              {hasUpdateActivityPermission && (
                <>
                  {(currentActions.visibleActions.length > 0 || loadBalancingParams) && (
                    <Divider orientation="vertical" flexItem />
                  )}
                  <IconButton size="small" onClick={handleMenuClick}>
                    <Iconify icon="mdi:more-horiz" />
                  </IconButton>
                  <Menu
                    anchorEl={menuAnchorEl}
                    open={menuOpen}
                    onClose={handleMenuClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <MenuItem
                      onClick={handleEditClick}
                      disabled={isProcessing}
                      sx={{
                        '&.Mui-disabled': {
                          opacity: 0.5,
                        },
                      }}
                    >
                      <Iconify icon="solar:pen-bold" sx={{ mr: 1 }} />
                      <Typography fontSize={14}>{t('form.header.edit')}</Typography>
                    </MenuItem>
                    {currentActions.menuActions.length > 0 && <Divider />}
                    {currentActions.menuActions.map((action) => {
                      const isPreRequestOrRequest = ('id' in action && (action.id === 'preRequest' || action.id === 'request'));
                      const disabled =
                        isProcessing ||
                        action.disabled ||
                        (isPreRequestOrRequest && requestJustSucceeded);
                      const menuItem = (
                        <MenuItem
                          key={action.label}
                          onClick={() => {
                            action.onClick();
                            handleMenuClose();
                          }}
                          disabled={disabled}
                          sx={{
                            '&.Mui-disabled': {
                              opacity: 0.5,
                            },
                          }}
                        >
                          <Iconify
                            icon={action.icon}
                            sx={{ mr: 1 }}
                            color={
                              disabled
                                ? 'grey.500'
                                : action.icon === 'ic:outline-delete'
                                  ? 'error.dark'
                                  : ''
                            }
                          />
                          <Typography
                            color={
                              disabled
                                ? 'grey.500'
                                : action.icon === 'ic:outline-delete'
                                  ? 'error.dark'
                                  : ''
                            }
                            fontSize={14}
                          >
                            {action.label}
                          </Typography>
                        </MenuItem>
                      );

                      if (!action.disabled || !action.disabledTooltip) {
                        return menuItem;
                      }

                      return (
                        <Tooltip key={action.label} title={action.disabledTooltip}>
                          <span>{menuItem}</span>
                        </Tooltip>
                      );
                    })}
                  </Menu>
                </>
              )}
            </Box>
          ) : null
        }
      />
      <ConfirmDialog
        open={confirmDelete}
        onClose={handleCancelDelete}
        title={t('actionPanel.confirmDeleteTitle')}
        content={t('actionPanel.confirmDeleteContent', {
          count: 1,
        })}
        action={
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>
            {t('actionPanel.delete')}
          </Button>
        }
      />
      <ConfirmDialog
        open={confirmUnallocate}
        onClose={handleCancelUnallocate}
        title={t('actionPanel.confirmUnallocateDependentsTitle')}
        content={t('actionPanel.confirmUnallocateDependentsContent')}
        action={
          <Button variant="contained" color="warning" onClick={handleConfirmUnallocate}>
            {t('actionPanel.unallocate')}
          </Button>
        }
      />
      <ConfirmDialog
        open={confirmCancel}
        onClose={handleCancelCancelDialog}
        title={t('actionPanel.confirmCancelTitle')}
        content={t('actionPanel.confirmCancelContent')}
        closeLabel={t('actionPanel.confirmCancelNo')}
        action={
          <Button variant="contained" color="error" onClick={handleConfirmCancel}>
            {t('actionPanel.confirmCancelYes')}
          </Button>
        }
      />
      <NonCompliantApprovalDialog
        open={confirmNonCompliant}
        feedback={nonCompliantFeedback}
        confirming={isProcessing}
        onClose={handleCancelNonCompliant}
        onConfirm={handleConfirmNonCompliant}
      />
      {openEditModal && (
        <ActivityNewEditForm
          id={visit?.id}
          open={openEditModal}
          onClose={handleCloseEditModal}
        />
      )}
      <CompetencyModal
        open={openCompetenciesModal}
        onClose={() => setOpenCompetenciesModal(false)}
        code={resolveActivitySchemeLabel(visit) || ''}
        onSave={handleSaveCompetencies}
        initialCompetencies={visit?.activityCompetencies?.map(mapActivityCompetencyToModalItem)}
        activityId={visit?.id}
        hasLevels
        standardTemplateId={resolveActivityStandardTemplateId(visit)}
      />
    </>
  );
}
